import { ExternalVendor } from '@interfaces/external-vendor.interface';

/// Whether a church proves who they are with a redirect flow or a key they
/// paste in. Providers differ, and the two need different connect flows.
export enum IntegrationAuthorizationStyle {
  OAUTH = 'OAUTH',
  API_KEY = 'API_KEY'
}

export enum IntegrationStatus {
  ACTIVE = 'ACTIVE',
  /// The provider rejected our refresh token. Only the church can fix this, by
  /// reconnecting.
  NEEDS_REAUTHORIZATION = 'NEEDS_REAUTHORIZATION',
  REVOKED = 'REVOKED'
}

export enum IntegrationWebhookStatus {
  NOT_REGISTERED = 'NOT_REGISTERED',
  ACTIVE = 'ACTIVE',
  FAILED = 'FAILED'
}

/// A church's connection to an outside system, without the credentials: the
/// API deliberately keeps those in a separate record this route never reads.
export interface IntegrationConnection {
  churchID: string;
  provider: ExternalVendor;
  status: IntegrationStatus;
  scope: string;
  organizationID: string;
  organizationName?: string | null;
  /// ISO 8601.
  connectedAt: string;
  connectedByStaffID?: string | null;
  lastSuccessfulSyncAt?: string | null;
  webhookStatus: IntegrationWebhookStatus;
  authorizationStyle: IntegrationAuthorizationStyle;
  /// ISO 8601, or null if the church hasn't imported people from this provider.
  /// Permanent, unlike import job records, which expire after six months.
  peopleImportCompletedAt?: string | null;
}

// The integrations envelope is `{ result: [...] }`. Read defensively so a
// shape we don't recognise reads as "nothing connected" rather than throwing.
export function integrationsFromResponse(body: unknown): IntegrationConnection[] {
  if (!body || typeof body !== 'object') {
    return [];
  }

  const payload = body as Record<string, any>;
  const candidate = payload.result ?? payload.results ?? payload;

  if (!Array.isArray(candidate)) {
    return [];
  }

  return candidate.filter((connection) => Boolean(connection?.provider)) as IntegrationConnection[];
}

// Only a connection the API calls ACTIVE is usable; one needing
// reauthorization can't make a request until the church reconnects.
export function isIntegrationUsable(connection?: IntegrationConnection | null) {
  return connection?.status === IntegrationStatus.ACTIVE;
}

// MARK: - People imports

/// A preview runs the whole import and writes no people. Only an apply imports
/// anyone, and only a finished apply counts as the church having imported.
export enum PeopleImportMode {
  PREVIEW = 'PREVIEW',
  APPLY = 'APPLY'
}

export enum PeopleImportStatus {
  QUEUED = 'QUEUED',
  RUNNING = 'RUNNING',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
  /// Stopped cleanly partway -- usually a chunk reaching its deadline -- and
  /// picks up again from where it left off.
  INTERRUPTED = 'INTERRUPTED'
}

export interface PeopleImportCounters {
  discovered: number;
  created: number;
  /// Existing Salt people matched by email and linked to the provider.
  claimed: number;
  updated: number;
  unchanged: number;
  skipped: number;
  failed: number;
}

export interface PeopleImportJob {
  id: string;
  churchID: string;
  provider: ExternalVendor;
  mode: PeopleImportMode;
  status: PeopleImportStatus;
  counters: PeopleImportCounters;
  estimatedTotal?: number | null;
  /// ISO 8601.
  createdAt: string;
  startedAt?: string | null;
  finishedAt?: string | null;
  failure?: {
    code: string;
    message: string;
  } | null;
}

export function isPeopleImportFinished(status: PeopleImportStatus) {
  return status === PeopleImportStatus.SUCCEEDED ||
    status === PeopleImportStatus.FAILED ||
    status === PeopleImportStatus.CANCELED;
}

// Everyone the import has dealt with so far, whatever happened to them.
export function peopleImportProcessedCount(counters: PeopleImportCounters) {
  return counters.created + counters.claimed + counters.updated +
    counters.unchanged + counters.skipped + counters.failed;
}

// MARK: - Team imports

/// A team in the church's Planning Center organization, offered for import.
export interface ImportableTeam {
  externalID: string;
  name: string;
  /// Tells apart same-named teams in different service types: two "Band"s is
  /// normal.
  serviceTypeName?: string | null;
  /// The Salt team it was imported as, or null if it hasn't been.
  importedTeamID?: string | null;
}
